import React, { useState, useEffect } from 'react';
import axiosInstance from "api/axios";
import {
    FaLeaf,
    FaRecycle,
    FaRegFileAlt,
    FaTrash,
    FaExclamationCircle,
} from 'react-icons/fa';
import debounce from 'lodash/debounce';
import './BinTable.css';

const SkeletonLoader = () => (
    <div className="animate-pulse flex space-x-4">
        <div className="flex-1 bg-gray-300 h-6 rounded" />
        <div className="flex-1 bg-gray-300 h-6 rounded" />
        <div className="flex-1 bg-gray-300 h-6 rounded" />
        <div className="flex-1 bg-gray-300 h-6 rounded" />
    </div>
);

const renderAlertIcon = (fillLevel) => {
    const threshold = 80;
    return fillLevel > threshold ? (
        <FaExclamationCircle className="text-red-500 ml-1" />
    ) : null;
};

const FillLevelBar = ({ type, level, icon: Icon }) => (
    <div className="flex flex-col space-y-1 w-1/4 px-1">
        <div className="flex items-center justify-between">
            <div className="flex items-center">
                <Icon className="mr-1 w-3 h-3" />
                <span className="text-xs truncate">{type}</span>
            </div>
            <span className="text-xs font-bold text-green-600">{level}%</span>
            {renderAlertIcon(level)}
        </div>
        <div className="w-full bg-gray-200 rounded-full h-1.5">
            <div
                className="bg-green-600 h-1.5 rounded-full"
                style={{ width: `${level}%` }}
            ></div>
        </div>
    </div>
);

export default function Table({ BinUniqueIdentifier }) {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isFirstLoad, setIsFirstLoad] = useState(true);

    useEffect(() => {
        const fetchData = debounce(async () => {
            try {
                setLoading(true);
                const response = await axiosInstance.get('/getlivechartfilllevels');
                if (response.data) {
                    setData(response.data);
                }
                setLoading(false);
                if (isFirstLoad) {
                    setIsFirstLoad(false);
                }
            } catch (error) {
                setError(error);
                setLoading(false);
            }
        }, 300); // 300ms debounce

        fetchData();

        const intervalId = setInterval(fetchData, 15000);

        return () => {
            clearInterval(intervalId);
            fetchData.cancel(); // Cancel debounced function on unmount
        };
    }, [isFirstLoad]);

    if (isFirstLoad && loading) return <SkeletonLoader />;
    if (error) {
        console.error('Error fetching data:', error);
    }
    if (!data) return <div>No data available</div>;

    const binData = data.find(bin => bin.id === BinUniqueIdentifier);
    if (!binData) return null;

    return (
        <div className="flex -mx-1">
            <FillLevelBar type="Organic" level={binData.fillLevels.organic} icon={FaLeaf} />
            <FillLevelBar type="PMD" level={binData.fillLevels.pmd} icon={FaRecycle} />
            <FillLevelBar type="Paper" level={binData.fillLevels.paper} icon={FaRegFileAlt} />
            <FillLevelBar type="Residual" level={binData.fillLevels.residual} icon={FaTrash} />
        </div>
    );
}
